import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Switch
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon 
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useSecteur } from '../../contexts/SecteurContext';

function SectorManagement() {
  const { allSecteurs, refreshSecteurs } = useSecteur();
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showInactiveSectors, setShowInactiveSectors] = useState(false);
  const [currentSector, setCurrentSector] = useState({
    id: undefined,
    name: '',
    description: '',
    is_active: false
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [orderBy, setOrderBy] = useState('name');
  const [orderDirection, setOrderDirection] = useState('asc');

  // Supprimer les log de debug
  useEffect(() => {
    refreshSecteurs();
  }, [refreshSecteurs]); 

  const handleOpenDialog = (sector = null) => {
    if (sector) {
      setCurrentSector({
        id: sector.id,
        name: sector.name,
        description: sector.description || '',
        is_active: sector.is_active || false
      });
      setIsEditing(true);
    } else {
      setCurrentSector({
        id: undefined,
        name: '',
        description: '',
        is_active: false
      });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentSector({
      id: undefined,
      name: '',
      description: '',
      is_active: false
    });
    setIsEditing(false);
  };

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    
    setCurrentSector(prev => {
      switch(name) {
        case 'name':
          return { ...prev, name: value };
        case 'description':
          return { ...prev, description: value };
        case 'is_active':
          return { ...prev, is_active: checked };
        default:
          return prev;
      }
    });
  };

  const handleSubmit = async () => {
    try {
      if (!currentSector.name) {
        throw new Error('Veuillez saisir un nom de secteur');
      }

      const sectorData = {
        name: currentSector.name,
        description: currentSector.description,
        is_active: currentSector.is_active
      };

      let result;
      if (isEditing && currentSector.id) {
        result = await supabase
          .from('sectors')
          .update(sectorData)
          .eq('id', currentSector.id);
      } else {
        result = await supabase
          .from('sectors')
          .insert([sectorData]);
      }

      if (result.error) throw result.error;

      handleCloseDialog();
      refreshSecteurs(); 
      showSnackbar(
        isEditing ? 'Secteur mis à jour avec succès' : 'Secteur créé avec succès',
        'success'
      );
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showSnackbar(error.message || 'Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleSort = (column) => {
    // Si la colonne est déjà triée, inverser la direction
    // Sinon, commencer par un tri ascendant
    if (orderBy === column) {
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrderDirection('asc');
    }
  };

  const handleToggleActive = async (sector) => {
    try {
      const { error } = await supabase
        .from('sectors')
        .update({ is_active: !sector.is_active })
        .eq('id', sector.id);

      if (error) throw error;

      refreshSecteurs(); 
      showSnackbar(
        sector.is_active 
          ? 'Secteur désactivé avec succès' 
          : 'Secteur réactivé avec succès', 
        'success'
      );
    } catch (error) {
      console.error('Erreur lors de la modification du statut:', error);
      showSnackbar(error.message || 'Erreur lors de la modification du statut', 'error');
    }
  };

  const handleDeleteSector = async (sectorId) => {
    try {
      // Vérifier d'abord s'il y a des mandats liés
      const { count, error: checkError } = await supabase
        .from('user_mandates')
        .select('*', { count: 'exact' })
        .eq('sector_id', sectorId);

      if (checkError) throw checkError;

      if (count > 0) {
        // S'il y a des mandats liés, désactiver le secteur au lieu de le supprimer
        const { error: updateError } = await supabase
          .from('sectors')
          .update({ is_active: false })
          .eq('id', sectorId);

        if (updateError) throw updateError;

        showSnackbar('Le secteur a été désactivé car il est encore utilisé', 'warning');
      } else {
        // Supprimer si aucun mandat lié
        const { error: deleteError } = await supabase
          .from('sectors')
          .delete()
          .eq('id', sectorId);

        if (deleteError) throw deleteError;

        showSnackbar('Secteur supprimé avec succès', 'success');
      }

      refreshSecteurs();
    } catch (error) {
      console.error('Erreur lors de la suppression du secteur:', error);
      showSnackbar(error.message || 'Erreur lors de la suppression du secteur', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ width: '100%', padding: 2 }}>
      <Box sx={{ 
        marginBottom: 2,
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h4">Secteurs</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => {
            setCurrentSector({
              id: undefined,
              name: '',
              description: '',
              is_active: true
            });
            setIsEditing(false);
            setOpenDialog(true);
          }}
          size="small"
        >
          Secteur
        </Button>
      </Box>
        
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        marginBottom: 2 
      }}>
        <Typography>Secteurs Inactifs</Typography>
        <Switch
          checked={showInactiveSectors}
          onChange={() => setShowInactiveSectors(!showInactiveSectors)}
          color="primary"
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell onClick={() => handleSort('name')}>
                Nom
                {orderBy === 'name' && (
                  orderDirection === 'asc' ? <ExpandLessIcon /> : <ExpandMoreIcon />
                )}
              </TableCell>
              <TableCell onClick={() => handleSort('description')}>
                Description
                {orderBy === 'description' && (
                  orderDirection === 'asc' ? <ExpandLessIcon /> : <ExpandMoreIcon />
                )}
              </TableCell>
              <TableCell onClick={() => handleSort('mandate_count')}>
                Nombre de Mandats
                {orderBy === 'mandate_count' && (
                  orderDirection === 'asc' ? <ExpandLessIcon /> : <ExpandMoreIcon />
                )}
              </TableCell>
              <TableCell onClick={() => handleSort('is_active')}>
                Statut
                {orderBy === 'is_active' && (
                  orderDirection === 'asc' ? <ExpandLessIcon /> : <ExpandMoreIcon />
                )}
              </TableCell>
              <TableCell align="left">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allSecteurs
              .sort((a, b) => {
                if (orderBy === 'name') {
                  return orderDirection === 'asc' 
                    ? a.name.localeCompare(b.name) 
                    : b.name.localeCompare(a.name);
                } else if (orderBy === 'description') {
                  return orderDirection === 'asc' 
                    ? (a.description || '').localeCompare(b.description || '') 
                    : (b.description || '').localeCompare(a.description || '');
                } else if (orderBy === 'mandate_count') {
                  return orderDirection === 'asc' 
                    ? (a.mandate_count - b.mandate_count)
                    : (b.mandate_count - a.mandate_count);
                } else if (orderBy === 'is_active') {
                  return orderDirection === 'asc' 
                    ? (a.is_active === b.is_active ? 0 : (a.is_active ? -1 : 1))
                    : (a.is_active === b.is_active ? 0 : (a.is_active ? 1 : -1));
                }
                return 0;
              })
              .filter(sector => showInactiveSectors || sector.is_active)
              .map((sector) => (
                <TableRow 
                  key={sector.id} 
                  sx={{ 
                    backgroundColor: sector.is_active ? 'inherit' : '#f5f5f5',
                    opacity: sector.is_active ? 1 : 0.7 
                  }}
                >
                  <TableCell>{sector.name}</TableCell>
                  <TableCell>{sector.description}</TableCell>
                  <TableCell>{sector.mandate_count}</TableCell>
                  <TableCell>
                    <Switch
                      checked={sector.is_active}
                      onChange={() => handleToggleActive(sector)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="left">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleOpenDialog(sector)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteSector(sector.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog pour ajouter/éditer un secteur */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? 'Modifier le secteur' : 'Ajouter un secteur'}
        </DialogTitle>
        <DialogContent>
          <TextField
            name="name"
            label="Nom du secteur"
            fullWidth
            margin="normal"
            value={currentSector.name}
            onChange={handleChange}
            required
          />
          <TextField
            name="description"
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={3}
            value={currentSector.description}
            onChange={handleChange}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 2 }}>
            <Typography>Actif</Typography>
            <Switch
              name="is_active"
              checked={currentSector.is_active || false}
              onChange={handleChange}
              color="primary"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Annuler
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            {isEditing ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SectorManagement;
